import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Play, Clock, Loader2, Tv } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function TVShow() {
  const { slug } = useParams();
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/tv/shows/${slug}`)
      .then(({ data }) => {
        setShow(data.show);
        setEpisodes(data.episodes || []);
      })
      .catch(() => setError("Show not found."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-ink flex items-center justify-center" data-testid="tv-show-loading">
        <Loader2 size={24} className="animate-spin text-gold-400 mr-3" />
        <span className="text-white/60 uppercase tracking-[0.22em] text-sm">Loading show…</span>
      </div>
    );
  }

  if (error || !show) {
    return (
      <div className="min-h-[70vh] bg-ink flex flex-col items-center justify-center px-6 text-center" data-testid="tv-show-not-found">
        <Tv size={38} className="text-gold-500/60 mb-5" />
        <p className="font-serif text-3xl text-white">Show not found</p>
        <p className="mt-3 text-white/50 text-sm">This series may have moved to a new slot.</p>
        <Link to="/tv" className="mt-8 inline-flex items-center gap-2 border border-gold-500/40 text-white/85 hover:text-gold-300 hover:border-gold-400 px-6 py-3 text-sm uppercase tracking-[0.16em] transition-colors">
          <ArrowLeft size={15} /> Back to HBTV
        </Link>
      </div>
    );
  }

  const heroEp = episodes[0];

  return (
    <div data-testid="tv-show-page" className="bg-ink text-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={show.cover_image} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/60 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24">
          <Link
            to="/tv"
            data-testid="tv-show-back"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 mb-8 transition-colors"
          >
            <ArrowLeft size={13} /> Back to HBTV
          </Link>

          <div className="grid gap-8 lg:grid-cols-12 items-end">
            <div className="lg:col-span-8 max-w-2xl">
              <span className="inline-flex items-center gap-2 border border-gold-500/40 bg-ink/60 backdrop-blur px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.26em] text-gold-300 mb-6">
                {show.category} · HBTV Original
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] text-white" data-testid="tv-show-title">
                {show.title}
              </h1>
              <p className="mt-5 text-xl md:text-2xl text-gold-300 font-accent italic">{show.tagline}</p>
              <p className="mt-6 text-white/70 leading-relaxed max-w-xl">{show.description}</p>
              <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-white/45">
                Hosted by <span className="text-white/75">{show.host}</span> · {episodes.length} episode{episodes.length === 1 ? "" : "s"}
              </p>
              {heroEp && (
                <Link
                  to={`/tv/watch/${heroEp.id}`}
                  data-testid="tv-show-play-latest"
                  className="mt-9 inline-flex items-center gap-2.5 bg-gold-500 hover:bg-gold-400 text-ink font-semibold uppercase tracking-[0.14em] text-sm px-8 py-4 transition-colors"
                >
                  <Play size={16} className="fill-current" strokeWidth={0} /> Play Latest Episode
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Episodes */}
      <section className="border-t border-gold-500/15 py-16 md:py-20" data-testid="tv-show-episodes">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal className="mb-10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-500 mb-3">Episode List</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">Every episode of <span className="italic font-accent text-gold-gradient">{show.title}</span></h2>
          </Reveal>

          {episodes.length === 0 ? (
            <div className="py-16 text-center text-white/50">No episodes released yet. Check back soon.</div>
          ) : (
            <div className="border border-gold-500/20 divide-y divide-gold-500/10">
              {episodes.map((e, i) => (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: i * 0.04, duration: 0.6 }}
                >
                  <Link
                    to={`/tv/watch/${e.id}`}
                    data-testid={`tv-episode-${e.id}`}
                    className="group flex items-start md:items-center gap-4 md:gap-6 p-4 md:p-5 hover:bg-ink-paper/50 transition-colors duration-200"
                  >
                    <span className="hidden md:block font-serif text-3xl text-gold-500/70 w-14 shrink-0">
                      {String(e.episode_number).padStart(2, "0")}
                    </span>
                    <div className="relative w-32 md:w-52 aspect-video shrink-0 overflow-hidden border border-gold-500/20">
                      <img src={e.thumbnail} alt={e.title} className="w-full h-full object-cover transition-transform duration-[700ms] group-hover:scale-[1.06]" />
                      <div className="absolute inset-0 bg-ink/20 group-hover:bg-ink/40 transition-colors" />
                      <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gold-500 text-ink flex items-center justify-center">
                          <Play size={16} className="ml-0.5 fill-current" strokeWidth={0} />
                        </span>
                      </span>
                      {e.duration && (
                        <span className="absolute bottom-1.5 right-1.5 bg-ink/80 border border-gold-500/25 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] text-white/80">
                          {e.duration}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif text-white text-lg md:text-2xl leading-tight line-clamp-2">{e.title}</p>
                      <p className="mt-1.5 text-white/60 text-sm leading-relaxed line-clamp-2">{e.description}</p>
                      {e.duration && (
                        <p className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/40 md:hidden">
                          <Clock size={11} /> {e.duration}
                        </p>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
